type CleanupFn = () => void;

type ComponentActions<TElement extends HTMLElement = HTMLElement, TBlock extends HTMLElement = HTMLElement> = {
  unload?: CleanupFn;
  select?: () => CleanupFn | void;
  reorder?: () => void;
  selectBlock?: (block: TBlock) => CleanupFn | void;
};

type InitComponentFn<TElement extends HTMLElement = HTMLElement, TBlock extends HTMLElement = HTMLElement> = (
  element: TElement
) => ComponentActions<TElement, TBlock> | void;

type InitComponentOptions = {
  deferred?: boolean;
};

type ShopifyBlockEditorData = {
  id?: string;
};

type ShopifyBlockEventDetail = {
  blockId?: string;
};

type ShopifyBlockEvent = Event & {
  detail?: ShopifyBlockEventDetail;
};

const componentActions = new WeakMap<HTMLElement, ComponentActions<any, any>>();
const componentSelectCleanupFns = new WeakMap<HTMLElement, CleanupFn>();
const blockSelectCleanupFns = new WeakMap<HTMLElement, CleanupFn>();

function isElement(target: EventTarget | null): target is HTMLElement {
  return target instanceof HTMLElement;
}

function parseSection(evt: Event): HTMLElement | null {
  if (!isElement(evt.target)) return null;
  const target = evt.target;

  if (!target.id) return null;
  return target.matches(".shopify-section") ? target : null;
}

function parseBlock(evt: ShopifyBlockEvent): HTMLElement | null {
  if (!isElement(evt.target)) return null;

  return evt.target;
}

function parseBlockSection(block: HTMLElement | null, selector: string): HTMLElement | null {
  if (!block) return null;

  const section = block.closest(".shopify-section");
  if (!(section instanceof HTMLElement)) return null;

  return section.querySelector(selector) ? section : null;
}

export function initComponent<TElement extends HTMLElement = HTMLElement, TBlock extends HTMLElement = HTMLElement>(
  componentSelector: string,
  initFn: InitComponentFn<TElement, TBlock>,
  options: InitComponentOptions = {}
): CleanupFn {
  const deferred = options.deferred ?? true;

  function initElement(el: HTMLElement): void {
    if (componentActions.has(el)) return;

    const actions = initFn(el as TElement) ?? {};
    componentActions.set(el, actions);
  }

  function initComponentsInSection(section: HTMLElement): void {
    const components = section.querySelectorAll<HTMLElement>(componentSelector);
    components.forEach((el) => {
      initElement(el);
    });
  }

  function unloadComponentsInSection(section: HTMLElement): void {
    const components = section.querySelectorAll<HTMLElement>(componentSelector);

    components.forEach((el) => {
      const actions = componentActions.get(el);
      actions?.unload?.();
      componentActions.delete(el);
    });
  }

  function onSectionLoad(evt: Event): void {
    const section = parseSection(evt);
    if (!section) return;

    initComponentsInSection(section);
  }

  function onSectionUnload(evt: Event): void {
    const section = parseSection(evt);
    if (!section) return;

    unloadComponentsInSection(section);
  }

  function onSectionSelect(evt: Event): void {
    const section = parseSection(evt);
    if (!section) return;

    const components = section.querySelectorAll<HTMLElement>(componentSelector);

    components.forEach((el) => {
      const actions = componentActions.get(el);
      if (!actions?.select) return;

      const cleanup = actions.select();
      if (!cleanup) return;

      componentSelectCleanupFns.set(el, cleanup);
    });
  }

  function onSectionDeselect(evt: Event): void {
    const section = parseSection(evt);
    if (!section) return;

    const components = section.querySelectorAll<HTMLElement>(componentSelector);

    components.forEach((el) => {
      const cleanup = componentSelectCleanupFns.get(el);
      if (!cleanup) return;

      cleanup();
      componentSelectCleanupFns.delete(el);
    });
  }

  function onSectionReorder(evt: Event): void {
    const section = parseSection(evt);
    if (!section) return;

    const components = section.querySelectorAll<HTMLElement>(componentSelector);

    components.forEach((el) => {
      const actions = componentActions.get(el);
      actions?.reorder?.();
    });
  }

  function onBlockSelect(evt: Event): void {    
    const block = parseBlock(evt as ShopifyBlockEvent);
    if (!block) return;

    const section = parseBlockSection(block, componentSelector);
    if (!section) return;

    const components = section.querySelectorAll<HTMLElement>(componentSelector);

    components.forEach((el) => {
      const actions = componentActions.get(el);
      if (!actions?.selectBlock) return;

      const cleanup = actions.selectBlock(block as TBlock);
      if (!cleanup) return;

      blockSelectCleanupFns.set(block, cleanup);
    });
  }

  function onBlockDeselect(evt: Event): void {
    const block = parseBlock(evt as ShopifyBlockEvent);
    if (!block) return;

    const cleanup = blockSelectCleanupFns.get(block);
    if (!cleanup) return;

    cleanup();
    blockSelectCleanupFns.delete(block);
  }

  function init(): CleanupFn {
    document.addEventListener("shopify:section:load", onSectionLoad);
    document.addEventListener("shopify:section:unload", onSectionUnload);
    document.addEventListener("shopify:section:select", onSectionSelect);
    document.addEventListener("shopify:section:deselect", onSectionDeselect);
    document.addEventListener("shopify:section:reorder", onSectionReorder);
    document.addEventListener("shopify:block:select", onBlockSelect);
    document.addEventListener("shopify:block:deselect", onBlockDeselect);

    document.querySelectorAll<HTMLElement>(".shopify-section").forEach((section) => {
      initComponentsInSection(section);
    });

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;

          if (node.matches(componentSelector)) {
            initElement(node);
            return;
          }

          node.querySelectorAll<HTMLElement>(componentSelector).forEach((el) => {
            initElement(el);
          });
        });
      }
    });

    if (document.documentElement) {      
      observer.observe(document.documentElement, { childList: true, subtree: true });
    } else {      
      window.addEventListener('DOMContentLoaded', () => {
        observer.observe(document.documentElement, { childList: true, subtree: true });
      });
    }

    return () => {
      document.querySelectorAll<HTMLElement>(".shopify-section").forEach((section) => {
        unloadComponentsInSection(section);
      });

      document.removeEventListener("shopify:section:load", onSectionLoad);
      document.removeEventListener("shopify:section:unload", onSectionUnload);
      document.removeEventListener("shopify:section:select", onSectionSelect);
      document.removeEventListener("shopify:section:deselect", onSectionDeselect);
      document.removeEventListener("shopify:section:reorder", onSectionReorder);
      document.removeEventListener("shopify:block:select", onBlockSelect);
      document.removeEventListener("shopify:block:deselect", onBlockDeselect);

      observer.disconnect();
    };
  }

  if (!deferred) {
    return init();
  }

  let deferredCleanup: CleanupFn | null = null;

  const timeout = window.setTimeout(() => {
    deferredCleanup = init();
  }, 0);

  return () => {
    deferredCleanup?.();
    window.clearTimeout(timeout);
  };
}